import { useState, useRef, useEffect } from "react";
import { BaseEditableItem, EditState, CommonHandlers } from "./commonTypes";

interface UseEditStateOptions<T extends BaseEditableItem> {
  items: T[];
  onItemsChange?: (items: T[]) => void;
  createNewItem: () => T;
  setItems: (items: T[]) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
}

export function useEditState<T extends BaseEditableItem>({
  items,
  onItemsChange,
  createNewItem,
  setItems,
  containerRef,
}: UseEditStateOptions<T>): EditState<T> &
  CommonHandlers<T> & {
    setEditingItem: (item: T | null) => void;
  } {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<T | null>(null);

  // Auto scroll to bottom when editing a new item
  useEffect(() => {
    if (editingIndex === items.length - 1 && containerRef?.current) {
      setTimeout(() => {
        containerRef.current?.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [editingIndex, items.length, containerRef]);

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    const item = items[index];
    setEditingItem({ ...item });
  };

  const handleDelete = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    onItemsChange?.(newItems);
  };

  const handleSave = (index: number) => {
    if (editingItem) {
      const newItems = [...items];
      newItems[index] = editingItem;
      setItems(newItems);
      onItemsChange?.(newItems);
      setEditingIndex(null);
      setEditingItem(null);
    }
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditingItem(null);
  };

  const handleCreate = () => {
    const newItem = createNewItem();
    setItems([...items, newItem]);
    setEditingIndex(items.length);
    setEditingItem(newItem);
  };

  return {
    editingIndex,
    editingItem,
    setEditingItem,
    handleEdit,
    handleDelete,
    handleSave,
    handleCancel,
    handleCreate,
  };
}
